import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { punchApi, projectsApi, type PunchItem, type PunchListResponse, type PunchPin } from '@/lib/apiClient';
import { resolveDownloadUrl } from '@/lib/graphUpload';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { Button } from '@/components';
import { PunchStyles } from './punchStyles';
import {
  STAGE_LABELS, STAGE_ORDER, summarize, bySub, matches, sortItems, hasFilters, locationLabel, shortDate,
  NO_FILTERS, filterTrades, tradeName, stamp, sheetGroups, untickChanged, assigneeKey, type BulkOutcome, type PunchFilters, type StatusFilter, type BicFilter,
} from './punchModel';
import { StatusBadge, BicChip, Stepper } from './punchUi';
import PunchItemDrawer from './PunchItemDrawer';
import PunchAddItemModal from './PunchAddItemModal';
import PunchSettingsModal from './PunchSettingsModal';
import PunchPublishModal from './PunchPublishModal';
import PunchSheets from './PunchSheets';
import PunchCloseoutReport from './PunchCloseoutReport';
// The drawing viewer brings PDF.js with it: loaded the first time it opens,
// inside a boundary so a failed load can't blank the page.
const PunchDrawingView = lazy(() => import('./PunchDrawingView'));
// And the phone's drawing screen, the same way.
const PunchPhoneDrawing = lazy(() => import('./PunchPhoneDrawing'));
import PunchDrawingBoundary from './PunchDrawingBoundary';
import PunchBulkBar, { BulkOutcomeNote } from './PunchBulkBar';
import PunchPhoneList from './PunchPhoneList';
import PunchPhoneAdd from './PunchPhoneAdd';
import { usePhoneLayout } from './usePhoneLayout';
import { useOutdoorMode } from './useOutdoorMode';

/**
 * The Punch List tab (#518, #524, #526): a job's list of unfinished or wrong
 * work, each item assigned to the party fixing it and tracked — with whose
 * move it is (ball in court) — until it is fixed, verified and closed.
 *
 * Desktop layout per the design handoff: stage banner, KPI tiles, the ball in
 * court and by-subcontractor cards (both filter the table), a filter bar, and
 * the item table; a row opens the item drawer (?item=<id>, so it can be
 * linked). Every rule is the API's; `access` from the API decides which
 * buttons show, so nothing appears that the server would refuse.
 */

export const punchKey = (projectId: number) => ['punch', projectId] as const;

export default function PunchTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const query = useQuery({ queryKey: punchKey(projectId), queryFn: () => punchApi.get(projectId) });
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber?: string | null; jobFolderUrl?: string | null; title?: string | null }>,
    staleTime: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: punchKey(projectId) });
    qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    qc.invalidateQueries({ queryKey: ['projects', 'activity', projectId] });
  };

  const start = useMutation({ mutationFn: () => punchApi.start(projectId), onSuccess: invalidate });

  const openItemId = Number(params.get('item')) || null;
  const openItem = (id: number | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'punch');
    if (id) next.set('item', String(id)); else next.delete('item');
    setParams(next, { replace: true });
  };

  const [modal, setModal] = useState<'add' | 'settings' | 'publish' | null>(null);
  /** The open sub sheets: an assignee key, 'all', or closed. */
  const [sheet, setSheet] = useState<string | null>(null);
  // Sheets for a bulk selection only (the bulk bar's "Sheets for These").
  const [sheetIds, setSheetIds] = useState<number[] | null>(null);
  const openSheets = (key: string, ids: number[] | null = null) => { setSheetIds(ids); setSheet(key); };
  // The close-out report (desktop, once the list is closed out).
  const [report, setReport] = useState(false);
  // The drawings (#518 phase 3), maybe on one item's pin or to place it; and a
  // new item dropped on a drawing, for the add form. The desktop's viewer or the
  // phone's screen is decided once, when it opens, as the add form is: re-deciding
  // on every render would swap them when a tablet turns across 768px and lose
  // the sheet, the zoom and a pin being placed.
  const [drawing, setDrawing] = useState<{ focus: { itemId: number; place: boolean } | null; phone: boolean } | null>(null);
  const [pinned, setPinned] = useState<{ pin: PunchPin; room: string | null; where: string } | null>(null);
  const tabGraph = useGraphToken();
  // Taken away by a reopen for good: closing out again later must not bring
  // it back up unasked (review).
  const stage = query.data?.list?.stage;
  useEffect(() => { if (stage !== 'closed_out') setReport(false); }, [stage]);
  // Phone width: the list grouped by room and the step-by-step add. The room
  // Add Here was tapped in, if it was.
  const phone = usePhoneLayout();
  // High contrast for the sun, on the phone screens only.
  const [outdoor] = useOutdoorMode();
  const [addRoom, setAddRoom] = useState<string | undefined>(undefined);
  // Which add form is decided once, when it opens: re-deciding on every render
  // would swap forms when the screen crosses 768px (an iPad in portrait is
  // exactly 768 and rotates), dropping what was entered and the retry-safe
  // request id, so a retry after a lost reply could add the item twice.
  const [addOnPhone, setAddOnPhone] = useState(false);
  const openModal = (m: 'add' | 'settings' | 'publish') => { if (m === 'add') setAddOnPhone(phone); setModal(m); };
  const openAdd = (room?: string) => { setAddRoom(room); openModal('add'); };

  // The sheets' data, rebuilt only when the list or the selection changes —
  // not on every render of this tab (review).
  const sheetData = useMemo(() => {
    const d = query.data;
    if (!d?.list) return null;
    return { ...d, list: d.list, items: sheetIds ? d.items.filter((i) => sheetIds.includes(i.id)) : d.items };
  }, [query.data, sheetIds]);

  if (query.isLoading) return <div className="pl"><PunchStyles /><p className="pl-muted">Loading the punch list…</p></div>;
  if (query.isError || !query.data) {
    return (
      <div className="pl">
        <PunchStyles />
        <div className="pl-error">{(query.error as Error | null)?.message ?? 'Could not load the punch list.'}</div>
      </div>
    );
  }

  const data = query.data;
  const canWork = data.access.canWork && !locked;
  const canManage = data.access.canManage && !locked;
  const jobNumber = projectQuery.data?.jobNumber ?? '';
  const jobFolderUrl = projectQuery.data?.jobFolderUrl ?? null;

  return (
    <div className={`pl${phone && outdoor ? ' pl-outdoor' : ''}`}>
      <PunchStyles />
      {!data.list ? (
        <EmptyState
          canStart={canWork}
          starting={start.isPending}
          error={start.isError ? (start.error as Error).message : null}
          onStart={() => start.mutate()}
        />
      ) : phone ? (
        <PunchPhoneList
          data={data as PunchListResponse & { list: NonNullable<PunchListResponse['list']> }}
          canWork={canWork}
          jobFolderUrl={jobFolderUrl}
          onOpenItem={openItem}
          onAdd={openAdd}
          onDrawings={() => setDrawing({ focus: null, phone: true })}
        />
      ) : (
        <PunchListView
          data={data as PunchListResponse & { list: NonNullable<PunchListResponse['list']> }}
          canWork={canWork}
          canManage={canManage}
          jobFolderUrl={jobFolderUrl}
          onOpenItem={openItem}
          onModal={openModal}
          onSheet={openSheets}
          onReport={() => setReport(true)}
          onDrawings={() => setDrawing({ focus: null, phone: false })}
          projectId={projectId}
          onChanged={invalidate}
        />
      )}

      {data.list && openItemId && (
        <PunchItemDrawer
          // Keyed by item: opening another item (a feed link, the URL) must
          // not carry the first one's half-filled edit, reject or photo panel.
          key={openItemId}
          projectId={projectId}
          itemId={openItemId}
          data={data}
          canWork={canWork}
          canManage={canManage}
          jobNumber={jobNumber}
          jobFolderUrl={jobFolderUrl}
          onClose={() => openItem(null)}
          onChanged={invalidate}
          onDrawing={(itemId, place) => { openItem(null); setDrawing({ focus: { itemId, place }, phone }); }}
        />
      )}
      {data.list && modal === 'add' && addOnPhone && (
        <PunchPhoneAdd
          // A new form for a new pin, as on the desktop.
          key={pinned ? `${pinned.pin.drawingSetId}:${pinned.pin.page}:${pinned.pin.x}:${pinned.pin.y}` : 'add'}
          projectId={projectId}
          data={data as PunchListResponse & { list: NonNullable<PunchListResponse['list']> }}
          jobNumber={jobNumber}
          jobFolderUrl={jobFolderUrl}
          initialRoom={addRoom}
          pinned={pinned}
          // Started from the phone's drawing (Add Item Here): over it, and back to it after.
          onTop={!!drawing}
          onClose={() => { setModal(null); setPinned(null); }}
          onAdded={invalidate}
        />
      )}
      {data.list && modal === 'add' && !addOnPhone && (
        <PunchAddItemModal
          // A new form for a new pin: one dropped on the drawing while the
          // form was already open mustn't keep the first pin.
          key={pinned ? `${pinned.pin.drawingSetId}:${pinned.pin.page}:${pinned.pin.x}:${pinned.pin.y}` : 'add'}
          projectId={projectId}
          data={data}
          jobNumber={jobNumber}
          jobFolderUrl={jobFolderUrl}
          pinned={pinned}
          onClose={() => { setModal(null); setPinned(null); }}
          onAdded={invalidate}
        />
      )}
      {data.list && modal === 'settings' && (
        <PunchSettingsModal projectId={projectId} data={data} onClose={() => setModal(null)} onSaved={invalidate} />
      )}
      {sheetData && sheet && (
        <PunchSheets
          projectId={projectId}
          data={sheetData}
          jobNumber={jobNumber}
          jobTitle={projectQuery.data?.title ?? ''}
          canWork={canWork}
          initial={sheet}
          onClose={() => { setSheet(null); setSheetIds(null); }}
          onSent={invalidate}
        />
      )}
      {data.list && drawing && (
        <PunchDrawingBoundary onClose={() => setDrawing(null)}>
          <Suspense fallback={null}>
            {drawing.phone ? (
              <PunchPhoneDrawing
                projectId={projectId}
                data={data as PunchListResponse & { list: NonNullable<PunchListResponse['list']> }}
                canWork={canWork}
                canAdd={!!jobFolderUrl}
                focus={drawing.focus}
                paused={modal === 'add'}
                getPdfUrl={(set) => resolveDownloadUrl(set.webUrl, tabGraph.silent)}
                needsSignIn={tabGraph.needsSignIn}
                onSignIn={tabGraph.interactive}
                onOpenItem={(id) => { setDrawing(null); openItem(id); }}
                // The phone's step-by-step add, over the drawing: it carries the pin.
                onAddAt={(pin, room, where) => { setPinned({ pin, room, where }); setAddRoom(undefined); setAddOnPhone(true); setModal('add'); }}
                onChanged={invalidate}
                // Back on a phone goes back where it came from: opened from an item
                // (View on Drawing, Place on Drawing, Move Pin), that's the item, not
                // the list (review).
                onClose={() => { const from = drawing.focus?.itemId; setDrawing(null); if (from) openItem(from); }}
              />
            ) : (
            <PunchDrawingView
              projectId={projectId}
              data={data as PunchListResponse & { list: NonNullable<PunchListResponse['list']> }}
              canWork={canWork}
              canAdd={!!jobFolderUrl}
              focus={drawing.focus}
              paused={modal === 'add'}
              // Silent here (the viewer opening isn't a click, so a popup would
              // be blocked); the viewer signs in from its own button (review).
              getPdfUrl={(set) => resolveDownloadUrl(set.webUrl, tabGraph.silent)}
              needsSignIn={tabGraph.needsSignIn}
              onSignIn={tabGraph.interactive}
              onOpenItem={(id) => { setDrawing(null); openItem(id); }}
              // The desktop form, always: it carries the pin, and the phone's step-by-step
              // add doesn't — a window narrowed while the viewer is open would drop it.
              onAddAt={(pin, room, where) => { setPinned({ pin, room, where }); setAddOnPhone(false); setModal('add'); }}
              onChanged={invalidate}
              onClose={() => setDrawing(null)}
            />
            )}
          </Suspense>
        </PunchDrawingBoundary>
      )}
      {/* Only while closed out: an item reopened meanwhile takes the report away. */}
      {data.list?.stage === 'closed_out' && report && (
        <PunchCloseoutReport
          projectId={projectId}
          trades={data.trades}
          ewCrewLabel={data.ewCrewLabel}
          jobNumber={jobNumber}
          jobTitle={projectQuery.data?.title ?? ''}
          jobLoaded={projectQuery.isSuccess}
          jobError={projectQuery.isError}
          onClose={() => { setReport(false); invalidate(); }}
        />
      )}
      {data.list && modal === 'publish' && (
        <PunchPublishModal projectId={projectId} data={data} onClose={() => setModal(null)} onPublished={invalidate} />
      )}
    </div>
  );
}

function EmptyState({ canStart, starting, error, onStart }: {
  canStart: boolean; starting: boolean; error: string | null; onStart: () => void;
}) {
  return (
    <div className="pl-card pl-empty">
      <div className="pl-empty-tile" aria-hidden>☑</div>
      <h3>No punch list yet</h3>
      <p>
        Start the E&amp;W internal punch when the building is ready to walk. Items stay internal until you
        publish the list after the architect/owner walk.
      </p>
      {canStart
        ? <Button variant="primary" isLoading={starting} onClick={onStart}>Start Internal Punch</Button>
        : <p className="pl-muted">Ask the project manager or the job's field leader to start it.</p>}
      {error && <div className="pl-error">{error}</div>}
    </div>
  );
}

function PunchListView({ data, canWork, canManage, jobFolderUrl, onOpenItem, onModal, onSheet, onReport, onDrawings, projectId, onChanged }: {
  data: PunchListResponse & { list: NonNullable<PunchListResponse['list']> };
  canWork: boolean;
  canManage: boolean;
  jobFolderUrl: string | null;
  onOpenItem: (id: number) => void;
  onModal: (m: 'add' | 'settings' | 'publish') => void;
  onSheet: (key: string, ids?: number[] | null) => void;
  onReport: () => void;
  onDrawings: () => void;
  projectId: number;
  onChanged: () => void;
}) {
  const { list, items } = data;
  const [filters, setFilters] = useState<PunchFilters>(NO_FILTERS);
  const set = (patch: Partial<PunchFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const toggle = <K extends keyof PunchFilters>(k: K, v: PunchFilters[K]) =>
    setFilters((f) => ({ ...f, [k]: f[k] === v ? NO_FILTERS[k] : v }));

  const s = useMemo(() => summarize(items), [items]);
  const subs = useMemo(() => bySub(items), [items]);
  const sheetKeys = useMemo(() => new Set(sheetGroups(items).map((g) => g.key)), [items]);
  const shown = useMemo(() => sortItems(items.filter((i) => matches(i, filters))), [items, filters]);
  const tradeLabel = (key: string | null) => tradeName(data.trades, key);

  // Bulk selection (design "Bulk bar"): only rows on screen count as selected,
  // so a row a filter hides is left out of a bulk change — a change never
  // lands on an item the user can't see. It comes back picked if the filter
  // shows it again.
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  const selectedIds = useMemo(() => shown.filter((i) => picked.has(i.id)).map((i) => i.id), [shown, picked]);
  const allPicked = shown.length > 0 && selectedIds.length === shown.length;
  const togglePick = (id: number) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Only the rows on screen: picks a filter hides are kept, so they come back
  // when shown again (Codex on #577).
  const toggleAll = () => setPicked((p) => {
    const n = new Set(p);
    for (const i of shown) { if (allPicked) n.delete(i.id); else n.add(i.id); }
    return n;
  });
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);
  const tradeOptions = useMemo(() => filterTrades(data.trades, items), [data.trades, items]);

  // Previews: the first before photo of each shown item, looked up silently.
  const graph = useGraphToken();
  const thumbUrls = useMemo(
    () => shown.map((i) => i.media.find((m) => m.kind === 'before')?.webUrl).filter((u): u is string => !!u),
    [shown],
  );
  const { thumbs, reset } = useThumbnailCache(thumbUrls, graph.silent);

  const stageIndex = STAGE_ORDER.indexOf(list.stage);
  const pct = s.total ? Math.round((s.closed / s.total) * 100) : 0;
  const ao = list.requiresAoApproval;

  return (
    <>
      {list.stage === 'closed_out' ? <ClosedOut data={data} onReport={onReport} /> : (<>
      <div className="pl-title">
        <h2>Punch List</h2>
        <span className="pl-meta">{s.total} item{s.total === 1 ? '' : 's'}</span>
      </div>

      <section className="pl-card pl-stage" aria-label="List stage">
        <div className="pl-stage-top">
          <div>
            <Stepper steps={STAGE_ORDER.map((st) => STAGE_LABELS[st])} current={stageIndex} />
            <div className="pl-stage-desc">
              {list.stage === 'internal'
                ? 'Our own list, worked before the architect/owner walk. Subs can get sheets now.'
                : `Formal list after the architect/owner walk${list.walkDate ? ` on ${shortDate(list.walkDate)}` : ''}. Carried-over items kept their numbers.`}
            </div>
          </div>
          <div className="pl-stage-actions">
            {canManage && <Button variant="secondary" onClick={() => onModal('settings')}>Settings</Button>}
            <Button variant="secondary" onClick={onDrawings}>Drawings</Button>
            <Button variant="secondary" disabled={sheetKeys.size === 0}
              title={sheetKeys.size === 0 ? 'No New, Open or Rejected items are assigned to anyone yet.' : undefined}
              onClick={() => onSheet('all')}>
              Sub Sheets
            </Button>
            {canManage && list.stage === 'internal' && (
              <Button variant="primary" onClick={() => onModal('publish')}>Publish List…</Button>
            )}
          </div>
        </div>
        <div className="pl-progress">
          <div className="pl-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="pl-progress-label">{s.closed} of {s.total} closed</span>
        </div>
        {canManage && list.stage === 'published' && s.closed === s.total && <CloseOutPrompt data={data} />}
      </section>

      <div className="pl-kpis">
        <Kpi label="Open" value={s.open} accent="var(--ew-performance)"
          sub={s.notSent ? `${s.notSent} not sent to subs yet` : 'All sent'}
          active={filters.status === 'open'} onClick={() => toggle('status', 'open')} />
        <Kpi label="Ready for review" value={s.readyForReview} accent="var(--ew-quality)" sub="Waiting on E&W to verify"
          active={filters.status === 'ready_for_review'} onClick={() => toggle('status', 'ready_for_review')} />
        {ao && (
          <Kpi label="Awaiting approval" value={s.awaitingApproval} accent="var(--ew-n-300)" sub="Architect/owner"
            active={filters.status === 'verified'} onClick={() => toggle('status', 'verified')} />
        )}
        <Kpi label="Closed" value={s.closed} accent="var(--ew-green)" sub={`${pct}% of ${s.total} items`}
          active={filters.status === 'closed'} onClick={() => toggle('status', 'closed')} />
        <Kpi label="Overdue" value={s.overdue} accent="var(--ew-danger)" danger={s.overdue > 0} sub="Past the due date"
          active={filters.status === 'overdue'} onClick={() => toggle('status', 'overdue')} />
      </div>

      <div className="pl-two">
        <section className="pl-card" aria-label="Ball in court">
          <div className="pl-card-hd pl-caption">Ball in court</div>
          <BicRow type="ew" label="Waiting on us" count={s.onUs.total}
            sub={`${s.onUs.toVerify} to verify · ${s.onUs.toSend} to send${s.onUs.withCrew ? ` · ${s.onUs.withCrew} with the crew` : ''}`}
            active={filters.bic === 'ew'} onClick={() => toggle('bic', 'ew')} />
          <BicRow type="assignee" label="Waiting on subs" count={s.onSubs.total}
            sub={`Across ${s.onSubs.subs} subcontractor${s.onSubs.subs === 1 ? '' : 's'}`}
            active={filters.bic === 'assignee'} onClick={() => toggle('bic', 'assignee')} />
          {ao ? (
            <BicRow type="ao" label="Waiting on Architect/Owner" count={s.onAo} sub="To approve"
              active={filters.bic === 'ao'} onClick={() => toggle('bic', 'ao')} />
          ) : (
            <p className="pl-muted" style={{ padding: '12px 16px', margin: 0 }}>
              Architect/Owner approval is off for this job. E&amp;W verification closes items.
            </p>
          )}
        </section>

        <section className="pl-card" aria-label="By subcontractor">
          <div className="pl-card-hd pl-caption">By subcontractor</div>
          <div className="pl-sub-grid pl-sub-head pl-caption">
            <span>Sub</span><span>Open</span><span>Review</span><span>Overdue</span><span>Closed</span>
          </div>
          <div className="pl-sub-list">
            {subs.length === 0 && <p className="pl-muted" style={{ padding: '12px 16px', margin: 0 }}>No items yet.</p>}
            {subs.map((r) => (
              <div key={r.key} className={`pl-sub-line${filters.sub === r.key ? ' active' : ''}`}>
                <button type="button" className="pl-sub-row pl-sub-grid"
                  onClick={() => toggle('sub', r.key)} aria-pressed={filters.sub === r.key}>
                  <span className="pl-sub-name">{r.label}</span>
                  <span>{r.open}</span>
                  <span>{r.review}</span>
                  <span className={r.overdue ? 'pl-overdue' : undefined}>{r.overdue || '—'}</span>
                  <span className="pl-mini-bar">
                    <span><span style={{ width: `${r.total ? (r.closed / r.total) * 100 : 0}%` }} /></span>
                    {r.closed}/{r.total}
                  </span>
                </button>
                {/* A sibling, not inside the row: a button can't hold a button. */}
                <Button variant="secondary" size="sm" style={{ width: 64, visibility: sheetKeys.has(r.key) ? 'visible' : 'hidden' }}
                  aria-label={`${r.label} sheet`} onClick={() => onSheet(r.key)}>
                  Sheet
                </Button>
              </div>
            ))}
          </div>
        </section>
      </div>
      </>)}

      <div className="pl-filters" role="search">
        <input className="pl-input search" placeholder="Search #, description, room" aria-label="Search items"
          value={filters.q} onChange={(e) => set({ q: e.target.value })} />
        <select className={`pl-select${filters.sub ? ' active' : ''}`} aria-label="Sub" value={filters.sub}
          onChange={(e) => set({ sub: e.target.value })}>
          <option value="">All subs</option>
          {subs.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select className={`pl-select${filters.trade ? ' active' : ''}`} aria-label="Trade" value={filters.trade}
          onChange={(e) => set({ trade: e.target.value })}>
          <option value="">All trades</option>
          {tradeOptions.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select className={`pl-select${filters.status ? ' active' : ''}`} aria-label="Status" value={filters.status}
          onChange={(e) => set({ status: e.target.value as StatusFilter })}>
          <option value="">All statuses</option>
          <option value="open">Open (incl. New and Rejected)</option>
          <option value="ready_for_review">Ready for review</option>
          {ao && <option value="verified">Verified</option>}
          <option value="closed">Closed</option>
          <option value="overdue">Overdue</option>
        </select>
        <select className={`pl-select${filters.bic ? ' active' : ''}`} aria-label="Ball in court" value={filters.bic}
          onChange={(e) => set({ bic: e.target.value as BicFilter })}>
          <option value="">Anyone's ball</option>
          <option value="ew">E&amp;W</option>
          <option value="assignee">Subs</option>
          {ao && <option value="ao">Architect/Owner</option>}
        </select>
        {hasFilters(filters) && <button type="button" className="pl-link" onClick={() => setFilters(NO_FILTERS)}>Clear filters</button>}
        <span className="pl-spacer" />
        <span className="pl-muted">{shown.length} of {s.total} items</span>
        {graph.needsSignIn && thumbUrls.length > 0 && (
          <Button variant="secondary" size="sm" onClick={async () => { try { await graph.interactive(); reset(); } catch { /* shown on next try */ } }}>
            Show previews
          </Button>
        )}
        {canWork && (
          <Button variant="secondary" disabled={!jobFolderUrl}
            title={jobFolderUrl ? undefined : 'Link this job’s Job Folder first — photos are saved there.'}
            onClick={() => onModal('add')}>
            Add Item
          </Button>
        )}
      </div>
      {canWork && !jobFolderUrl && (
        <div className="pl-warn">Link this project’s Job Folder on the Overview tab before adding items — photos are saved there.</div>
      )}

      {canWork && selectedIds.length > 0 && (
        <PunchBulkBar projectId={projectId} data={data} ids={selectedIds} onDone={onChanged}
          onSheets={() => onSheet('all', selectedIds)}
          onApplied={(results) => setPicked((p) => untickChanged(p, results))} onClear={() => setPicked(new Set())}
          onOutcome={setBulkOutcome} />
      )}
      {bulkOutcome && <BulkOutcomeNote outcome={bulkOutcome} onDismiss={() => setBulkOutcome(null)} />}

      <section className="pl-card" aria-label="Items">
        <div className="ew-table-scroll">
          <table className="pl-table">
            <thead>
              <tr>
                {canWork && (
                  <th className="pl-pick-col">
                    <input type="checkbox" aria-label="Select all shown" checked={allPicked}
                      ref={(el) => { if (el) el.indeterminate = selectedIds.length > 0 && !allPicked; }}
                      disabled={shown.length === 0} onChange={toggleAll} />
                  </th>
                )}
                <th>#</th><th>Photo</th><th>Description</th><th>Trade</th><th>Assigned to</th>
                <th>Ball in court</th><th>Status</th><th>Location</th><th>Last updated</th><th>Due</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={canWork ? 11 : 10} className="pl-muted" style={{ cursor: 'default' }}>
                  {s.total === 0 ? 'No items yet. Add the first one from the building walk.' : 'No items match these filters.'}
                </td></tr>
              )}
              {shown.map((i) => (
                <ItemRow key={i.id} item={i} thumb={thumbOf(i, thumbs)} trade={tradeLabel(i.trade)} onOpen={() => onOpenItem(i.id)}
                  pick={canWork ? { on: picked.has(i.id), toggle: () => togglePick(i.id) } : null} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function thumbOf(i: PunchItem, thumbs: Record<string, string>): string | null {
  const url = i.media.find((m) => m.kind === 'before')?.webUrl;
  return url ? thumbs[url] ?? null : null;
}

function ItemRow({ item, thumb, trade, onOpen, pick }: {
  item: PunchItem; thumb: string | null; trade: string; onOpen: () => void;
  pick: { on: boolean; toggle: () => void } | null;
}) {
  return (
    <tr className={pick?.on ? 'picked' : undefined} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); }}
      tabIndex={0} aria-label={`Open ${item.label}`}>
      {pick && (
        // The cell, not just the box, takes the click, and it never opens the row.
        <td className="pl-pick-col" onClick={(e) => { e.stopPropagation(); pick.toggle(); }}>
          <input type="checkbox" aria-label={`Select ${item.label}`} checked={pick.on}
            onClick={(e) => e.stopPropagation()} onChange={pick.toggle} />
        </td>
      )}
      <td className="ew-num">{item.label}</td>
      <td>{thumb ? <img className="pl-thumb" src={thumb} alt="" /> : <span className="pl-thumb" aria-hidden />}</td>
      <td className="pl-desc">{item.description}</td>
      <td>{trade}</td>
      <td>{item.assigneeLabel ?? <span className="pl-muted">Unassigned</span>}</td>
      <td><BicChip type={item.bic.type} label={item.bic.label} /></td>
      <td><StatusBadge status={item.status} /></td>
      <td>{locationLabel(item)}</td>
      <td className="pl-when" title={item.lastUpdatedBy ? `by ${item.lastUpdatedBy}` : undefined}>
        {stamp(item.lastUpdatedAt) || '—'}
      </td>
      <td className={item.overdue ? 'pl-overdue' : undefined}>{shortDate(item.dueDate) || '—'}</td>
    </tr>
  );
}

function Kpi({ label, value, sub, accent, active, onClick, danger = false }: {
  label: string; value: number; sub: string; accent: string; active: boolean; onClick: () => void; danger?: boolean;
}) {
  return (
    <button type="button" className={`pl-kpi-btn${active ? ' active' : ''}`} onClick={onClick} aria-pressed={active}>
      <div className="pw-kpi">
        <span className="pw-kpi-strip" style={{ background: accent }} />
        <span className="pw-kpi-label">{label}</span>
        <span className="pw-kpi-value" style={danger ? { color: 'var(--ew-danger-700)' } : undefined}>{value}</span>
        <span className="pl-kpi-sub">{sub}</span>
      </div>
    </button>
  );
}

function BicRow({ type, label, sub, count, active, onClick }: {
  type: 'ew' | 'assignee' | 'ao'; label: string; sub: string; count: number; active: boolean; onClick: () => void;
}) {
  const chip = type === 'ew' ? 'E&W' : type === 'ao' ? 'A/O' : 'Sub';
  return (
    <button type="button" className={`pl-bic-row${active ? ' active' : ''}`} onClick={onClick} aria-pressed={active}>
      <span style={{ textAlign: 'center' }}><BicChip type={type} label={chip} /></span>
      <span><span className="pl-bic-label">{label}</span><br /><span className="pl-bic-sub">{sub}</span></span>
      <span className="pl-bic-count">{count}</span>
    </button>
  );
}

/** Published and every item closed: offer the close-out (PM/PE). */
function CloseOutPrompt({ data }: { data: PunchListResponse & { list: NonNullable<PunchListResponse['list']> } }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: async () => {
      try {
        await punchApi.closeOut(data.list.projectId);
      } catch (e) {
        // A close-out whose reply was lost: its retry is refused as "already
        // closed out". Re-read before reporting (Codex on #565).
        const now = await punchApi.get(data.list.projectId).catch(() => null);
        if (now?.list?.stage !== 'closed_out') throw e;
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: punchKey(data.list.projectId) });
      qc.invalidateQueries({ queryKey: ['projects', 'activity', data.list.projectId] });
    },
  });
  return (
    <div className="pl-note pl-row" style={{ justifyContent: 'space-between' }}>
      <span>Every item is closed. Close out the list to make it the close-out record.</span>
      <Button variant="primary" size="sm" isLoading={m.isPending} onClick={() => m.mutate()}>Close Out List</Button>
      {m.isError && <span className="pl-error">{(m.error as Error).message}</span>}
    </div>
  );
}

function ClosedOut({ data, onReport }: { data: PunchListResponse & { list: NonNullable<PunchListResponse['list']> }; onReport: () => void }) {
  const { list, items } = data;
  const internal = items.filter((i) => i.origin === 'internal').length;
  // Counted as the close-out report groups them (assigneeKey: one sub however
  // its name is cased), and only subs — not the E&W crew (review).
  const subs = new Set(items.filter((i) => i.assignee?.type === 'sub').map((i) => assigneeKey(i.assignee))).size;
  return (
    <>
      <section className="pl-card pl-stage">
        <Stepper steps={STAGE_ORDER.map((st) => STAGE_LABELS[st])} current={2} />
      </section>
      <div className="pl-card pl-empty">
        <div className="pl-empty-tile done" aria-hidden>✓</div>
        <h3>All {items.length} items closed</h3>
        <p>
          Punch list closed out{list.closedOutAt ? ` ${shortDate(list.closedOutAt, { year: true })}` : ''}{list.closedOutBy ? ` by ${list.closedOutBy}` : ''}.
        </p>
        <p className="pl-muted">
          Internal punch {internal} · Added at owner walk {items.length - internal} · Subcontractors {subs}
        </p>
        <Button variant="primary" onClick={onReport}>Download Close-out Report</Button>
        <p className="pl-muted">Reopening an item below puts the list back to Published.</p>
      </div>
    </>
  );
}
